import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { FiPlus, FiFolder, FiTrash2, FiEdit2, FiX, FiBarChart2 } from "react-icons/fi";
import { useToast } from "../components/ToastProvider";
import ConfirmDialog from "../components/ConfirmDialog";
import { api } from "../utils/api";
import "../styles/Dashboard.css";
import "../styles/Campaigns.css";
import "../styles/Modal.css";

const PRESET_COLORS = [
  "#2383e2", "#0f7b6c", "#cb912f", "#d44040", "#7c3aed",
  "#0891b2", "#be185d", "#65a30d", "#6366f1", "#f59e0b",
];

export default function CampaignsPage() {
  const [campaigns, setCampaigns] = useState([]);
  const [analyticsByCampaign, setAnalyticsByCampaign] = useState({});
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  // Form State
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [color, setColor] = useState("#2383e2");
  const [saving, setSaving] = useState(false);

  const { addToast } = useToast();
  const navigate = useNavigate();

  const fetchCampaigns = useCallback(async () => {
    try {
      const data = await api.getCampaigns();
      const list = data.campaigns || [];
      setCampaigns(list);

      if (list.length > 0) {
        const analyticsEntries = await Promise.all(
          list.map(async (campaign) => {
            try {
              const analyticsData = await api.getCampaignAnalytics(campaign.$id);
              return [campaign.$id, analyticsData];
            } catch (err) {
              return [campaign.$id, null];
            }
          })
        );
        setAnalyticsByCampaign(Object.fromEntries(analyticsEntries));
      } else {
        setAnalyticsByCampaign({});
      }
    } catch (err) {
      console.error(err);
      addToast("Failed to load campaigns", "error");
    } finally {
      setLoading(false);
    }
  }, [addToast]);

  useEffect(() => {
    document.title = "Campaigns - LinkSquire";
    fetchCampaigns();
  }, [fetchCampaigns]);

  const openCreateModal = () => {
    setEditingCampaign(null);
    setName("");
    setDescription("");
    setColor("#2383e2");
    setIsModalOpen(true);
  };

  const openEditModal = (campaign, e) => {
    e.preventDefault();
    e.stopPropagation();
    setEditingCampaign(campaign);
    setName(campaign.name);
    setDescription(campaign.description || "");
    setColor(campaign.color || "#2383e2");
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingCampaign(null);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      addToast("Name is required", "error");
      return;
    }

    setSaving(true);
    try {
      if (editingCampaign) {
        await api.updateCampaign(editingCampaign.$id, { name, description, color });
        addToast("Campaign updated", "success");
      } else {
        await api.createCampaign({ name, description, color });
        addToast("Campaign created", "success");
      }
      closeModal();
      fetchCampaigns();
    } catch (error) {
      console.error(error);
      addToast(error.message || "Failed to save campaign", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    try {
      await api.deleteCampaign(deleteTarget.$id);
      addToast("Campaign deleted", "success");
      setDeleteTarget(null);
      fetchCampaigns();
    } catch (err) {
      console.error(err);
      addToast("Failed to delete campaign", "error");
    }
  };

  const handleDeleteClick = (campaign, e) => {
    e.preventDefault();
    e.stopPropagation();
    setDeleteTarget(campaign);
  };

  return (
    <div className="dashboard page-fade-in">
      <div className="dashboard-header">
        <div>
          <h1>Campaigns</h1>
          <p style={{ color: "var(--text-secondary)", fontSize: "14px", marginTop: 4 }}>
            Organize related links and track their aggregated performance together.
          </p>
        </div>
        <button className="btn-primary" onClick={openCreateModal}>
          <FiPlus /> Create Campaign
        </button>
      </div>

      {loading ? (
        <div className="loading-state">
          <div className="spinner" />
          <p>Loading campaigns...</p>
        </div>
      ) : campaigns.length === 0 ? (
        <div className="campaign-empty-state">
          <div className="campaign-empty-icon">
            <FiFolder size={32} />
          </div>
          <h3>Create your first campaign</h3>
          <p>
            Group related links together for marketing initiatives, product launches, or seasonal promotions to see their combined analytics.
          </p>
          <button className="btn-primary" onClick={openCreateModal}>
            <FiPlus /> Get Started
          </button>
        </div>
      ) : (
        <>
          <div className="campaign-count">
            {campaigns.length} campaign{campaigns.length !== 1 ? "s" : ""}
          </div>
          <div className="campaigns-grid">
            {campaigns.map((campaign) => (
              <div
                key={campaign.$id}
                className="campaign-card"
                onClick={() => navigate(`/campaigns/${campaign.$id}`)}
              >
                <div
                  className="campaign-card-accent"
                  style={{ backgroundColor: campaign.color || "#2383e2" }}
                />
                <div className="campaign-card-body">
                  <div className="campaign-card-header">
                    <div
                      className="campaign-card-dot"
                      style={{ backgroundColor: campaign.color || "#2383e2" }}
                    />
                    <div className="campaign-card-name">{campaign.name}</div>
                  </div>
                  {campaign.description && (
                    <div className="campaign-card-desc">{campaign.description}</div>
                  )}

                  <CampaignCardSnapshot analytics={analyticsByCampaign[campaign.$id]} />
                </div>
                <div className="campaign-card-footer">
                  <div className="campaign-card-date">
                    Created {new Date(campaign.created_at).toLocaleDateString()}
                  </div>
                  <div className="campaign-card-actions">
                    <button
                      onClick={(e) => openEditModal(campaign, e)}
                      className="btn-icon"
                      data-tooltip="Edit"
                    >
                      <FiEdit2 size={14} />
                    </button>
                    <button
                      onClick={(e) => handleDeleteClick(campaign, e)}
                      className="btn-icon btn-icon-danger"
                      data-tooltip="Delete"
                    >
                      <FiTrash2 size={14} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Create / Edit Modal */}
      {isModalOpen && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingCampaign ? "Edit Campaign" : "Create Campaign"}</h2>
              <button className="modal-close" onClick={closeModal}>
                <FiX size={18} />
              </button>
            </div>

            <form onSubmit={handleSave}>
              <div className="modal-body">
                <div className="form-group">
                  <label htmlFor="campaignName">Name <span className="required">*</span></label>
                  <input
                    id="campaignName"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="e.g., Summer Sale 2026"
                    required
                    autoFocus
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="campaignDesc">
                    Description{" "}
                    <span style={{ fontWeight: "normal", color: "var(--text-secondary)", textTransform: "none", letterSpacing: 0 }}>
                      (Optional)
                    </span>
                  </label>
                  <textarea
                    id="campaignDesc"
                    className="campaign-textarea"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Internal notes about this campaign"
                    rows="3"
                  />
                </div>

                <div className="form-group">
                  <label>Color Tag</label>
                  <div className="campaign-color-picker">
                    <div className="campaign-color-swatch">
                      <input
                        type="color"
                        value={color}
                        onChange={(e) => setColor(e.target.value)}
                      />
                    </div>
                    <input
                      type="text"
                      value={color}
                      onChange={(e) => setColor(e.target.value)}
                      placeholder="#2383e2"
                      style={{ flex: 1, textTransform: "uppercase" }}
                    />
                  </div>
                  <div className="campaign-color-presets">
                    {PRESET_COLORS.map((c) => (
                      <div
                        key={c}
                        className={`campaign-color-preset${color === c ? " active" : ""}`}
                        style={{ backgroundColor: c }}
                        onClick={() => setColor(c)}
                        data-tooltip={c.toUpperCase()}
                      />
                    ))}
                  </div>
                </div>
              </div>

              <div className="modal-footer">
                <button type="button" className="btn-secondary" onClick={closeModal} disabled={saving}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary" disabled={saving}>
                  {saving ? "Saving..." : editingCampaign ? "Update" : "Create"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete confirmation dialog */}
      {deleteTarget && (
        <ConfirmDialog
          title="Delete this campaign?"
          message={`"${deleteTarget.name}" will be permanently deleted. Links in this campaign will not be deleted, but they will be removed from the campaign.`}
          confirmLabel="Delete"
          cancelLabel="Cancel"
          variant="danger"
          onConfirm={handleDeleteConfirm}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
}

function CampaignCardSnapshot({ analytics }) {
  const summary = analytics?.summary || {};
  const detailed = analytics?.analytics || {};

  const totalClicks = summary.periodClicks || 0;
  const byDevice = Array.isArray(detailed.by_device) ? detailed.by_device : [];
  const byCountry = Array.isArray(detailed.by_country) ? detailed.by_country : [];
  const byReferrer = Array.isArray(detailed.by_referrer) ? detailed.by_referrer : [];
  const byBrowser = Array.isArray(detailed.by_browser) ? detailed.by_browser : [];

  const totalDeviceClicks = byDevice.reduce((sum, item) => sum + (item.count || 0), 0);
  const mobileClicks = byDevice
    .filter((item) => String(item.name || "").toLowerCase() === "mobile")
    .reduce((sum, item) => sum + (item.count || 0), 0);
  const mobileTrafficPct = totalDeviceClicks > 0 ? Math.round((mobileClicks / totalDeviceClicks) * 100) : 0;

  const topSource = byReferrer[0]?.name || "Direct";
  const topRegion = byCountry[0]?.name || "Unknown";
  const topDevice = byDevice[0]?.name || "Unknown";
  const topBrowser = byBrowser[0]?.name || "Unknown";

  return (
    <div className="campaign-card-snapshot" aria-label="Campaign snapshot preview">
      <div className="campaign-card-snapshot-top">
        <p>Campaign Snapshot</p>
        <span>Live</span>
      </div>

      <div className="campaign-card-snapshot-row">
        <div className="campaign-card-metric">
          <h4>{formatCompact(totalClicks)}</h4>
          <p>Clicks</p>
        </div>
        <div className="campaign-card-metric">
          <h4>{mobileTrafficPct}%</h4>
          <p>Mobile</p>
        </div>
      </div>

      <div className="campaign-card-snapshot-details">
        <div className="campaign-card-snapshot-item">
          <p>Top source</p>
          <span>{topSource}</span>
        </div>
        <div className="campaign-card-snapshot-item">
          <p>Top region</p>
          <span>{topRegion}</span>
        </div>
        <div className="campaign-card-snapshot-item">
          <p>Best device</p>
          <span>{`${topDevice} / ${topBrowser}`}</span>
        </div>
        <div className="campaign-card-snapshot-footer">
          <FiBarChart2 size={13} /> Hover preview
        </div>
      </div>
    </div>
  );
}

function formatCompact(value) {
  return new Intl.NumberFormat("en", {
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(value || 0);
}
